import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { createFamilyMember, getFamilyRelationshipTypes, axiosInstance, processSuggestionsBulk } from '../../services/api'
import { transformPayload } from '../../utils/familyApiTransform'
import SuggestionConfirmModal from './SuggestionConfirmModal'
import { getUser } from '../../utils/secureStorage'

/**
 * InviteFamilyModal - Unified form for adding family members
 *
 * Features:
 * - Can add members related to logged-in user OR other family members
 * - Minors (under 16) can only be added as related to logged-in user
 * - Minor status auto-detected from DOB or manually flagged
 */
function InviteFamilyModal({
  isOpen,
  onClose,
  familyId,
  preSelectedRelationshipType = null,
  selectedPersonId = null,
  selectedParentInfo = null,
  rootPersonId = null,
  onSuccess = null,
  anchorPersonName = ''
}) {
  const navigate = useNavigate()

  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    gender: '',
    relationship_type: '',
    related_to_person_id: ''
  })

  // Family members for "related to" dropdown
  const [familyMembers, setFamilyMembers] = useState([])
  const [isLoadingMembers, setIsLoadingMembers] = useState(true)

  // Relationship types
  const [relationshipTypes, setRelationshipTypes] = useState([])
  const [isLoadingRelationships, setIsLoadingRelationships] = useState(true)

  // UI state
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Suggestions modal state
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false)
  const [isProcessingSuggestions, setIsProcessingSuggestions] = useState(false)
  const [newPersonName, setNewPersonName] = useState('')

  // Tree-only state (for deceased/non-tech-savvy family members)
  const [addToTreeOnly, setAddToTreeOnly] = useState(false)
  const [isDeceased, setIsDeceased] = useState(false)
  const [yearOfPassing, setYearOfPassing] = useState('')

  // Minor state
  const [isMinorManual, setIsMinorManual] = useState(false) // Manual toggle
  const [calculatedAge, setCalculatedAge] = useState(null)
  const isMinorFromDOB = calculatedAge !== null && calculatedAge < 16
  const isMinor = isMinorManual || isMinorFromDOB

  // Get logged-in user info
  const getLoggedInUser = () => {
    try {
      const userData = getUser() || {}
      return {
        id: userData.person_id || userData.id || rootPersonId,
        name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'You'
      }
    } catch {
      return { id: rootPersonId, name: 'You' }
    }
  }

  const loggedInUser = getLoggedInUser()

  // Fallback relationship types
  // Only immediate family relationships
  const ALLOWED_NAMES = new Set(['mother', 'father', 'son', 'daughter', 'wife', 'husband']);

  const fallbackRelationshipTypes = [
    { id: 40, name: 'Father', reverse_name: 'Child', category: 'family' },
    { id: 39, name: 'Mother', reverse_name: 'Child', category: 'family' },
    { id: 43, name: 'Spouse', reverse_name: 'Spouse', category: 'family' },
    { id: 42, name: 'Son', reverse_name: 'Parent', category: 'family' },
    { id: 41, name: 'Daughter', reverse_name: 'Parent', category: 'family' },
    { id: 38, name: 'Brother', reverse_name: 'Brother', category: 'family' },
    { id: 37, name: 'Sister', reverse_name: 'Sister', category: 'family' },
  ]

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchFamilyMembers()
      fetchRelationshipTypes()

      // Set relationship type default (related_to_person_id is set in fetchFamilyMembers)
      if (preSelectedRelationshipType) {
        setFormData(prev => ({
          ...prev,
          relationship_type: preSelectedRelationshipType
        }))
      }
    }
  }, [isOpen])

  // Minors can be added by any adult family member as the parent.
  // No longer forced to logged-in user only — supports step-parent and
  // adding children on behalf of another family member.

  const fetchFamilyMembers = async () => {
    try {
      setIsLoadingMembers(true)

      // Get familyId from prop or localStorage
      const currentFamilyId = familyId || localStorage.getItem('familyId')
      if (!currentFamilyId) {
        throw new Error('No family ID found')
      }

      // Fetch family members from the API
      const response = await axiosInstance.get(`/families/${currentFamilyId}/members/`)
      const membersData = response.data.members || response.data.results || response.data || []

      // Filter out minors and transform to dropdown format
      let members = membersData
        .filter(member => !member.is_minor) // Only show adults
        .map(member => ({
          id: member.id,
          name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown',
          isLoggedInUser: member.id === loggedInUser.id
        }))

      // Always ensure logged-in user is in the list (they should be an adult)
      const hasLoggedInUser = members.some(m => m.id === loggedInUser.id)
      if (!hasLoggedInUser && loggedInUser.id) {
        members.unshift({ id: loggedInUser.id, name: loggedInUser.name, isLoggedInUser: true })
      }

      // If still empty, add logged-in user as fallback
      if (members.length === 0 && loggedInUser.id) {
        members = [{ id: loggedInUser.id, name: loggedInUser.name, isLoggedInUser: true }]
      }

      setFamilyMembers(members)

      // Set default value: use selectedPersonId prop if provided, otherwise logged-in user
      const defaultRelatedTo = selectedPersonId || loggedInUser.id
      if (defaultRelatedTo) {
        setFormData(prev => ({ ...prev, related_to_person_id: defaultRelatedTo }))
      }
    } catch (err) {
      console.error('Error fetching family members:', err)
      // Fallback: just show logged-in user
      if (loggedInUser.id) {
        setFamilyMembers([{ id: loggedInUser.id, name: loggedInUser.name, isLoggedInUser: true }])
        setFormData(prev => ({ ...prev, related_to_person_id: loggedInUser.id }))
      }
    } finally {
      setIsLoadingMembers(false)
    }
  }

  const fetchRelationshipTypes = async () => {
    try {
      setIsLoadingRelationships(true)
      const response = await getFamilyRelationshipTypes()

      let types = []
      if (response && Array.isArray(response)) {
        types = response
      } else if (response?.relationship_types) {
        types = response.relationship_types
      } else if (response?.results) {
        types = response.results
      }

      if (types.length === 0) {
        types = fallbackRelationshipTypes
      }

      // Filter to only immediate family relationships
      types = types.filter(t => ALLOWED_NAMES.has(t.name.toLowerCase()))
      if (types.length === 0) types = fallbackRelationshipTypes

      // Sort by proximity
      const sorted = sortRelationshipsByProximity(types)
      setRelationshipTypes(sorted)
    } catch (err) {
      console.error('Error fetching relationship types:', err)
      setRelationshipTypes(sortRelationshipsByProximity(fallbackRelationshipTypes))
    } finally {
      setIsLoadingRelationships(false)
    }
  }

  const sortRelationshipsByProximity = (types) => {
    const order = {
      'spouse': 1, 'husband': 1, 'wife': 1,
      'son': 2, 'daughter': 2, 'child': 2,
      'mother': 3, 'father': 3, 'parent': 3,
      'brother': 4, 'sister': 4, 'sibling': 4,
      'grandmother': 5, 'grandfather': 5,
      'grandson': 6, 'granddaughter': 6,
      'aunt': 7, 'uncle': 7,
      'niece': 8, 'nephew': 8,
      'cousin': 9
    }

    return types.sort((a, b) => {
      const aOrder = order[a.name.toLowerCase()] || 100
      const bOrder = order[b.name.toLowerCase()] || 100
      return aOrder !== bOrder ? aOrder - bOrder : a.name.localeCompare(b.name)
    })
  }

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) {
      setCalculatedAge(null)
      return
    }

    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    setCalculatedAge(age)
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target

    if (type === 'checkbox') {
      if (name === 'is_minor_manual') {
        setIsMinorManual(checked)
      }
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))

    if (name === 'date_of_birth') {
      calculateAge(value)
    }

    if (error) setError('')
    if (success) setSuccess('')
  }

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('Please enter a first name.')
      return false
    }

    if (!formData.last_name || !formData.last_name.trim()) {
      setError('Please enter a last name.')
      return false
    }

    if (!formData.date_of_birth) {
      setError('Please enter a date of birth so we can set up their story timeline.')
      return false
    }

    if (!formData.related_to_person_id) {
      setError('Please select who this person is related to.')
      return false
    }

    if (!formData.relationship_type) {
      setError('Please select the relationship type.')
      return false
    }

    // Email required for adults (unless tree-only)
    if (!isMinor && !addToTreeOnly && !formData.email) {
      setError('Email address is required for adults so we can send them an invitation.')
      return false
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address.')
      return false
    }

    // Validate year of passing if provided
    if (yearOfPassing) {
      const year = parseInt(yearOfPassing, 10)
      const currentYear = new Date().getFullYear()
      if (isNaN(year) || year < 1000 || year > currentYear) {
        setError(`Year of passing must be between 1000 and ${currentYear}`)
        return false
      }
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const selectedRelationType = relationshipTypes.find(t => t.id == formData.relationship_type)
      if (!selectedRelationType) {
        throw new Error('Invalid relationship type')
      }

      // Infer gender from relationship type
      const inferGender = (relName) => {
        const male = ['father', 'son', 'brother', 'grandfather', 'grandson', 'uncle', 'nephew', 'husband']
        const female = ['mother', 'daughter', 'sister', 'grandmother', 'granddaughter', 'aunt', 'niece', 'wife']
        const lower = relName.toLowerCase()
        if (male.some(r => lower.includes(r))) return 'male'
        if (female.some(r => lower.includes(r))) return 'female'
        return formData.gender || 'unknown'
      }

      const legacyPayload = {
        human: {
          email: addToTreeOnly ? null : (formData.email || null),
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          middle_name: null,
          date_of_birth: formData.date_of_birth || null,
          city_of_birth: null,
          country_of_birth: null,
          gender: inferGender(selectedRelationType.name),
          is_minor: addToTreeOnly ? false : isMinor,
          living: addToTreeOnly ? !isDeceased : true,
          is_tree_only: addToTreeOnly,
          ...(addToTreeOnly && isDeceased && { availability_status: 'DECEASED' }),
          ...(addToTreeOnly && isDeceased && yearOfPassing && { death_year: yearOfPassing })
        },
        relationship_type: {
          name: selectedRelationType.name,
          reverse_name: selectedRelationType.reverse_name || selectedRelationType.name
        },
        communication_initiated: false
      }

      const newFormatPayload = transformPayload(legacyPayload, formData.related_to_person_id, selectedParentInfo)

      const result = await createFamilyMember(newFormatPayload)

      // Store the name for the suggestions modal
      setNewPersonName(formData.first_name)

      // Check if there are inferred relationship suggestions
      if (result.inferred_suggestions && result.inferred_suggestions.length > 0) {
        setSuggestions(result.inferred_suggestions)
        setShowSuggestionsModal(true)
        setSuccess(`${formData.first_name} added! Review suggested family connections.`)
        // Don't close the modal yet - let the suggestions modal handle it
      } else if (addToTreeOnly) {
        setSuccess(`${formData.first_name} has been added to your family tree.`)

        // Reset form
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          date_of_birth: '',
          gender: '',
          relationship_type: '',
          related_to_person_id: loggedInUser.id
        })
        setIsMinorManual(false)
        setAddToTreeOnly(false)
        setIsDeceased(false)
        setYearOfPassing('')
        setCalculatedAge(null)

        if (onSuccess) onSuccess()

        setTimeout(() => {
          onClose(true)
        }, 1500)
      } else {
        // No suggestions - proceed normally
        if (formData.email) {
          setSuccess(`Family member added and invitation sent to ${formData.email}!`)
        } else {
          setSuccess('Family member added successfully!')
        }

        // Reset form
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          date_of_birth: '',
          gender: '',
          relationship_type: '',
          related_to_person_id: loggedInUser.id
        })
        setIsMinorManual(false)
        setAddToTreeOnly(false)
        setIsDeceased(false)
        setYearOfPassing('')
        setCalculatedAge(null)

        if (onSuccess) onSuccess()

        setTimeout(() => {
          onClose(true)
        }, 1500)
      }

    } catch (err) {
      console.error('Error creating family member:', err)
      setError(err.message || 'Failed to add family member. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Get the name of who this person is related to
  const getRelatedToName = () => {
    const member = familyMembers.find(m => m.id === formData.related_to_person_id)
    return member?.isLoggedInUser ? 'you' : member?.name || 'selected person'
  }

  // Handle accepting/rejecting suggestions
  const handleAcceptSuggestions = async ({ accept, reject }) => {
    setIsProcessingSuggestions(true)
    try {
      if (accept.length > 0 || reject.length > 0) {
        await processSuggestionsBulk({ accept, reject })
      }
      // Reset and close both modals
      setShowSuggestionsModal(false)
      setSuggestions([])
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        date_of_birth: '',
        gender: '',
        relationship_type: '',
        related_to_person_id: loggedInUser.id
      })
      setIsMinorManual(false)
      setAddToTreeOnly(false)
      setIsDeceased(false)
      setYearOfPassing('')
      setCalculatedAge(null)
      if (onSuccess) onSuccess()
      onClose(true)
    } catch (err) {
      console.error('Error processing suggestions:', err)
      // Still close even if suggestions fail
      setShowSuggestionsModal(false)
      if (onSuccess) onSuccess()
      onClose(true)
    } finally {
      setIsProcessingSuggestions(false)
    }
  }

  // Handle skipping suggestions
  const handleSkipSuggestions = () => {
    setShowSuggestionsModal(false)
    setSuggestions([])
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      date_of_birth: '',
      gender: '',
      relationship_type: '',
      related_to_person_id: loggedInUser.id
    })
    setIsMinorManual(false)
    setAddToTreeOnly(false)
    setIsDeceased(false)
    setYearOfPassing('')
    setCalculatedAge(null)
    if (onSuccess) onSuccess()
    onClose(true)
  }

  if (!isOpen) return null

  if (!familyId) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 text-center">
          <p className="text-gray-600 mb-4">No family access. Please log in again.</p>
          <button onClick={onClose} className="px-6 py-3 bg-gray-200 rounded-lg">Close</button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#556B2F] text-white p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-2xl font-bold mb-1">Add Family Member</h2>
          <p className="text-white/80">Add someone to your family tree</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Who is this person related to? */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <label className="block text-base font-bold text-gray-900 mb-2">
              Who is this person related to? <span className="text-red-600">*</span>
            </label>
            <select
              name="related_to_person_id"
              value={formData.related_to_person_id}
              onChange={handleInputChange}
              disabled={isLoadingMembers}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg bg-white focus:border-[#556B2F] focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
              required
            >
              <option value="">Select a family member...</option>
              {familyMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.isLoggedInUser ? `${member.name} (You)` : member.name}
                </option>
              ))}
            </select>
            {isMinor && formData.related_to_person_id && formData.related_to_person_id !== loggedInUser.id && (
              <p className="text-sm text-blue-700 mt-2">
                {getRelatedToName()}'s account will be set as this child's guardian.
              </p>
            )}
          </div>

          {/* Relationship Type */}
          <div>
            <label className="block text-base font-bold text-gray-900 mb-2">
              Relationship to {getRelatedToName()} <span className="text-red-600">*</span>
            </label>
            <select
              name="relationship_type"
              value={formData.relationship_type}
              onChange={handleInputChange}
              disabled={isLoadingRelationships}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg bg-white focus:border-[#556B2F] focus:outline-none"
              required
            >
              <option value="">Select relationship...</option>
              {relationshipTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {formData.related_to_person_id === loggedInUser.id ? `My ${type.name}` : type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Will this person join online? */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
            <label className="block text-base font-bold text-gray-900 mb-3">
              Will this person join online?
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
                <input
                  type="radio"
                  name="add_to_tree_only"
                  checked={!addToTreeOnly}
                  onChange={() => { setAddToTreeOnly(false); setIsDeceased(false); setYearOfPassing('') }}
                  className="mt-1 w-4 h-4 text-[#556B2F]"
                />
                <div>
                  <span className="text-base font-semibold text-gray-800">Yes, invite them</span>
                  <p className="text-sm text-gray-500">They will receive an email invitation</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
                <input
                  type="radio"
                  name="add_to_tree_only"
                  checked={addToTreeOnly}
                  onChange={() => { setAddToTreeOnly(true); setIsMinorManual(false) }}
                  className="mt-1 w-4 h-4 text-[#556B2F]"
                />
                <div>
                  <span className="text-base font-semibold text-gray-800">No, just add to tree</span>
                  <p className="text-sm text-gray-500">For family who won't use the app</p>
                </div>
              </label>
            </div>
          </div>

          {/* Deceased + Year of Passing (only for tree-only) */}
          {addToTreeOnly && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-gray-50 border-2 border-gray-200 rounded-xl">
                <input
                  type="checkbox"
                  id="is_deceased"
                  checked={isDeceased}
                  onChange={(e) => { setIsDeceased(e.target.checked); if (!e.target.checked) setYearOfPassing('') }}
                  className="w-5 h-5 text-[#556B2F] border-2 border-gray-300 rounded focus:ring-[#556B2F]"
                />
                <label htmlFor="is_deceased" className="text-base text-gray-800">
                  <strong>This person is deceased</strong>
                </label>
              </div>
              {isDeceased && (
                <div>
                  <label className="block text-base font-bold text-gray-700 mb-2">
                    Year of Passing <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    value={yearOfPassing}
                    onChange={(e) => setYearOfPassing(e.target.value)}
                    placeholder="e.g. 2019"
                    min="1000"
                    max={new Date().getFullYear()}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
                  />
                </div>
              )}
            </div>
          )}

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-[#556B2F]">Personal Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-bold text-gray-700 mb-2">
                  First Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-base font-bold text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
              />
              {calculatedAge !== null && (
                <p className="text-sm text-gray-600 mt-1">Age: {calculatedAge} years old</p>
              )}
            </div>

            {/* Minor Toggle (only for invite mode, not tree-only) */}
            {!addToTreeOnly && (
              <>
                <div className="flex items-center gap-3 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                  <input
                    type="checkbox"
                    id="is_minor_manual"
                    name="is_minor_manual"
                    checked={isMinorManual}
                    onChange={handleInputChange}
                    disabled={isMinorFromDOB}
                    className="w-5 h-5 text-[#556B2F] border-2 border-gray-300 rounded focus:ring-[#556B2F]"
                  />
                  <label htmlFor="is_minor_manual" className="text-base text-gray-800">
                    <strong>This is a minor</strong> (under 16 years old)
                  </label>
                </div>
                {isMinor && (
                  <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                    {isMinorFromDOB
                      ? 'This person is under 16 based on the date of birth entered.'
                      : 'You have marked this person as a minor.'}
                    {' '}Minors can only be added to your own profile and will not receive email invitations.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Contact Information (for adults in invite mode) */}
          {!isMinor && !addToTreeOnly && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#556B2F]">Contact Information</h3>
              <div>
                <label className="block text-base font-bold text-gray-700 mb-2">
                  Email Address <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="family.member@email.com"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
                  required={!isMinor && !addToTreeOnly}
                />
                <p className="text-sm text-gray-500 mt-1">
                  We'll send them an invitation to join your family.
                </p>
              </div>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-4 px-6 rounded-xl text-lg font-bold text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: '#E65100' }}
            >
              {isLoading ? 'Adding...' : 'Add Family Member'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-4 rounded-xl text-lg font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Suggestions Modal - renders on top of this modal */}
      {showSuggestionsModal && (
        <SuggestionConfirmModal
          suggestions={suggestions}
          newPersonName={newPersonName}
          onAccept={handleAcceptSuggestions}
          onSkip={handleSkipSuggestions}
          isLoading={isProcessingSuggestions}
        />
      )}
    </div>
  )
}

export default InviteFamilyModal
